import { ReasonToBuyUI } from "./ReasonToBuyUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { Variant } from "@/types/types";
import { TickCircleOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleOutlined";
import { DeliveryOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { GiftCardOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GiftCardOutlined";
import { GuaranteesAndTroubleshootingOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GuaranteesAndTroubleshootingOutlined";
import { ProtectPlusSvg } from "../PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg";
import { useMemo } from "react";

type VariantService = NonNullable<Variant["services"]>[number];
type IncludedGuaranteeService = Extract<
  VariantService,
  { __typename: "IncludedGuarantee" }
>;

function isIncludedGuarantee(
  service: VariantService,
): service is IncludedGuaranteeService {
  return service.__typename === "IncludedGuarantee";
}

export function ReasonToBuy({ product }: Pick<StandardProps, "product">) {
  const { variant } = useVariant(product);

  const includedGuarantee = variant.services?.find(isIncludedGuarantee);

  const protectPlus = useMemo(
    () =>
      variant.services?.filter(
        (service) =>
          service.__typename === "ExtendedWarranty" &&
          service.title.includes("Protect Plus"),
      ) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (
    !includedGuarantee ||
    (includedGuarantee?.title === "" && protectPlus.length === 0)
  ) {
    return null;
  }

  const cards = [
    {
      id: "delivery",
      title: "Free standard delivery over £50",
      description: "Otherwise £4.50",
      icon: <DeliveryOutlined size="large" />,
    },
    {
      id: "price-promise",
      title: "Our price promise",
      description: "Confidence in every purchase",
      icon: <TickCircleOutlined size="large" />,
    },
    {
      id: "split-the-cost",
      title: "Split the cost",
      description: "Flexible payments available",
      icon: <GiftCardOutlined size="large" />,
    },
  ];

  if (includedGuarantee && includedGuarantee.title !== "") {
    cards.push({
      id: "guarantee",
      title: includedGuarantee.title,
      description: "Included with your purchase",
      icon: <GuaranteesAndTroubleshootingOutlined size="large" />,
    });
  }

  if (protectPlus.length > 0) {
    cards.push({
      id: "protect-plus",
      title: "Protect Plus",
      description: "Our comprehensive protection plan",
      icon: <ProtectPlusSvg />,
    });
  }

  return <ReasonToBuyUI cards={cards} />;
}
